import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';
import { SkillListRowData } from '../skill-list-row/skill-list-row';

/**
 * Re-export SkillListRowData as SkillCardData for consistency
 */
export type SkillCardData = SkillListRowData;

/**
 * SkillCard Component
 *
 * Displays a skill's basic information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-skill-card
 *   [skill]="skillData"
 *   [editRoute]="['/skills-catalog', skillData.id]"
 *   (cardClick)="onSkillSelected($event)">
 * </zag-skill-card>
 * ```
 */
@Component({
  selector: 'zag-skill-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon],
  host: {
    '[class.zag-skill-card]': 'true',
  },
  template: `
    <zag-card variant="interactive" (cardClick)="onCardClick()">
      <!-- Header: Flag/Icon/Color, Name and optional Delete button -->
      <div card-header class="zag-skill-card__header">
        @if (skill().imageUrl) {
          <img class="zag-skill-card__flag" [src]="skill().imageUrl" [alt]="skill().name" aria-hidden="true" />
        } @else {
          <div
            class="zag-skill-card__icon-avatar zag-icon-surface-gradient"
            [class.zag-skill-card__icon-avatar--fallback]="!skill().color"
            [style.background-color]="skill().color || null"
          >
            <span [zagIcon]="skill().icon || 'psychology'" [style.color]="getIconColor(skill().color)" class="zag-skill-card__icon"></span>
          </div>
        }
        <div class="zag-skill-card__text">
          <h3 class="zag-card-title">{{ skill().name }}</h3>
        </div>
        @if (showDelete()) {
          <button
            type="button"
            class="zag-skill-card__delete-btn"
            [disabled]="deleteDisabled()"
            [attr.aria-label]="deleteAriaLabel() || null"
            (click)="onDeleteClick($event)"
          >
            <span zagIcon="delete"></span>
          </button>
        }
      </div>

      <!-- Body: Description -->
      @if (skill().description) {
        <p class="zag-skill-card__description">{{ skill().description }}</p>
      }
    </zag-card>
  `,
  styles: [
    `
      .zag-skill-card {
        display: flex;
        flex: 1;
        align-content: stretch;
        flex-wrap: nowrap;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
      }

      .zag-card {
        width: 100%;
      }

      .zag-skill-card__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        width: 100%;
      }

      .zag-skill-card__icon {
        font-size: var(--entity-avatar-icon-size-card);
        flex-shrink: 0;
      }

      .zag-skill-card__flag {
        width: var(--entity-avatar-size-card);
        height: var(--entity-avatar-size-card);
        border-radius: var(--border-radius-sm);
        object-fit: cover;
        flex-shrink: 0;
        border: 1px solid var(--border-color-default);
      }

      .zag-skill-card__icon-avatar {
        width: var(--entity-avatar-size-card);
        height: var(--entity-avatar-size-card);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-skill-card__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-skill-card__text {
        flex: 1;
        min-width: 0;
      }

      .zag-skill-card__description {
        color: var(--text-color-secondary);
        font-size: 0.875rem;
        margin: 0;
      }

      .zag-skill-card__delete-btn {
        margin-left: auto;
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color var(--transition-duration-fast, 0.15s),
          color var(--transition-duration-fast, 0.15s);

        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .zag-skill-card__delete-btn {
          transition-duration: 0.01ms;
        }
      }
    `,
  ],
})
export class SkillCard {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The skill data to display */
  readonly skill = input.required<SkillCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the card is clicked */
  readonly cardClick = output<SkillCardData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<SkillCardData>();

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.skill());
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.skill());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
